import { forwardRef, useMemo, useState } from 'react'
import NextImage, { ImageProps as NextImageProps } from 'next/image'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type ImageProps = Omit<NextImageProps, 'src' | 'alt'> &
  Partial<{
    source: Partial<WPImage>
    size: 'thumbnail' | 'medium' | 'large'
    fit: 'contain' | 'cover'
    alt: string
  }>

const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    { source, size = 'large', fit = 'cover', fill, alt, className, ...props },
    ref
  ) => {
    const [loaded, setLoaded] = useState(false)

    const [src, width, height] = useMemo(
      () => [
        source?.sizes?.[size]?.url || source?.url || '',
        source?.sizes?.[size]?.width || source?.width || 0,
        source?.sizes?.[size]?.height || source?.height || 0
      ],
      [source, size]
    )

    return (
      <NextImage
        ref={ref}
        src={src}
        fill={fill}
        alt={alt || ''}
        onLoad={() => setLoaded(true)}
        className={cn([
          'transition-opacity duration-300 ease-in-out-cubic',
          {
            'object-cover object-center': fit === 'cover',
            'object-contain object-center': fit === 'contain'
          },
          {
            'opacity-0': !loaded,
            'opacity-100': loaded
          },
          className
        ])}
        {...(!fill ? { width, height } : {})}
        {...props}
      />
    )
  }
)

export default Image
